// src/components/agent/TeamChat.js
// Floating team-chat bubble (bottom-right) for site-wide messaging
// between agents/admins. Single shared room — every active agent
// (not lead submitters) sees and writes here.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, query, orderBy, limit, onSnapshot, addDoc, serverTimestamp,
  doc, updateDoc, arrayUnion
} from 'firebase/firestore';
import { db } from '../../firebase';
import { playChime, ensureBrowserNotifyPermission, showBrowserNotification } from '../../lib/notify';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_LITE = '#E2C47A';
const CREAM = '#FAF7F2';
const BORDER = 'rgba(107,30,46,0.1)';

export default function TeamChat({ profile }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const initialLoad = useRef(true);
  const lastSeenIdRef = useRef(null);
  const scrollRef = useRef(null);

  // Subscribe to the last 100 messages.
  useEffect(() => {
    if (!profile?.uid) return;
    const q = query(collection(db, 'teamChat'), orderBy('createdAt', 'asc'), limit(200));
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // First load: don't ding for everything that already existed.
      if (initialLoad.current) {
        initialLoad.current = false;
        lastSeenIdRef.current = list.length ? list[list.length - 1].id : null;
        setMessages(list);
        return;
      }

      // New messages since last snapshot? Ding if any are from someone else.
      const newOnes = lastSeenIdRef.current
        ? list.filter((m, i) => i > list.findIndex(x => x.id === lastSeenIdRef.current))
        : list;
      const incoming = newOnes.filter(m => m.fromUid && m.fromUid !== profile.uid);
      if (incoming.length) {
        playChime('chat');
        const last = incoming[incoming.length - 1];
        showBrowserNotification(
          `${last.fromName || 'Teammate'} on HomeIQa`,
          last.text || '(message)',
          () => setOpen(true)
        );
      }
      lastSeenIdRef.current = list.length ? list[list.length - 1].id : null;
      setMessages(list);
    });
    return () => unsub();
  }, [profile?.uid]);

  // Ask for browser-notify permission lazily, the first time the chat opens.
  useEffect(() => {
    if (open) ensureBrowserNotifyPermission();
  }, [open]);

  // Auto-scroll to bottom on new messages while open.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages.length]);

  // Mark all visible incoming messages as read whenever the panel is open.
  useEffect(() => {
    if (!open || !profile?.uid) return;
    const toMark = messages.filter(m => m.fromUid !== profile.uid && !(m.readBy || []).includes(profile.uid));
    toMark.forEach(m => {
      updateDoc(doc(db, 'teamChat', m.id), { readBy: arrayUnion(profile.uid) }).catch(() => {});
    });
  }, [open, messages, profile?.uid]);

  const unreadCount = useMemo(() => {
    if (!profile?.uid) return 0;
    return messages.filter(m => m.fromUid !== profile.uid && !(m.readBy || []).includes(profile.uid)).length;
  }, [messages, profile?.uid]);

  // Expose unread count globally so the sidebar bell can read it without
  // re-subscribing to the same collection.
  useEffect(() => {
    window.__homeiqaChatUnread = unreadCount;
    window.dispatchEvent(new CustomEvent('homeiqa-chat-unread', { detail: unreadCount }));
  }, [unreadCount]);

  // Anyone signed in (admin, contract agent, lead submitter) gets the chat.
  if (!profile) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'teamChat'), {
        text,
        fromUid: profile.uid,
        fromName: profile.name || profile.email || 'Agent',
        fromEmail: profile.email || null,
        fromPhotoURL: profile.photoURL || null,
        readBy: [profile.uid],
        createdAt: serverTimestamp()
      });
      setInput('');
    } catch (e) {
      console.error('chat send failed', e);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Collapsed bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={s.bubble}
          aria-label="Open team chat"
          title="Team chat"
        >
          <span style={s.bubbleIcon}>💬</span>
          {unreadCount > 0 && (
            <span style={s.bubbleBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>
      )}

      {/* Expanded panel */}
      {open && (
        <div style={s.panel}>
          <header style={s.head}>
            <div>
              <div style={s.headTitle}>Team chat</div>
              <div style={s.headSub}>HomeIQa internal</div>
            </div>
            <button onClick={() => setOpen(false)} style={s.closeBtn} aria-label="Close">×</button>
          </header>

          <div ref={scrollRef} style={s.body}>
            {messages.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyTitle}>No messages yet</div>
                <div style={s.emptyDesc}>Say hi to your team. They'll hear a chime.</div>
              </div>
            ) : (
              messages.map(m => {
                const mine = m.fromUid === profile.uid;
                return (
                  <div key={m.id} style={{ ...s.msgRow, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div style={mine ? { ...s.msg, ...s.msgMine } : s.msg}>
                      {!mine && <div style={s.msgFrom}>{m.fromName || 'Agent'}</div>}
                      <div style={s.msgText}>{m.text}</div>
                      <div style={mine ? { ...s.msgTime, ...s.msgTimeMine } : s.msgTime}>
                        {fmtTime(m.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={s.inputRow}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type a message…"
              rows={1}
              style={s.input}
            />
            <button onClick={send} disabled={!input.trim() || sending} style={s.sendBtn}>
              {sending ? '…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
         d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const s = {
  bubble: {
    position: 'fixed', bottom: 22, right: 22, zIndex: 220,
    width: 56, height: 56, borderRadius: '50%',
    background: BURG_DK, border: `2px solid ${GOLD}`,
    boxShadow: '0 8px 24px rgba(74,18,32,0.35)',
    cursor: 'pointer', color: '#fff', fontSize: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif", padding: 0
  },
  bubbleIcon: { fontSize: 22, lineHeight: 1 },
  bubbleBadge: {
    position: 'absolute', top: -2, right: -2,
    minWidth: 20, height: 20, padding: '0 6px',
    background: GOLD, color: BURG_DK, fontWeight: 700,
    fontSize: 11, borderRadius: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `2px solid ${BURG_DK}`
  },

  panel: {
    position: 'fixed', bottom: 22, right: 22, zIndex: 220,
    width: 360, maxWidth: 'calc(100vw - 28px)',
    height: 540, maxHeight: 'calc(100vh - 80px)',
    background: '#fff', borderRadius: 14,
    border: `1px solid ${BORDER}`,
    boxShadow: '0 18px 56px rgba(74,18,32,0.28)',
    display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
    overflow: 'hidden'
  },
  head: {
    background: BURG_DK, color: '#fff',
    padding: '14px 18px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `2px solid ${GOLD}`
  },
  headTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: GOLD_LITE },
  headSub: { fontSize: 10, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.1em', textTransform: 'uppercase' },
  closeBtn: {
    background: 'transparent', color: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(255,255,255,0.2)', width: 28, height: 28, borderRadius: '50%',
    fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0
  },

  body: {
    flex: 1, overflowY: 'auto',
    padding: '14px 14px',
    background: CREAM,
    display: 'flex', flexDirection: 'column', gap: 8
  },
  empty: { textAlign: 'center', padding: '40px 16px', color: '#9A9A9A' },
  emptyTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: BURG_DK, marginBottom: 6 },
  emptyDesc: { fontSize: 12, color: '#9A9A9A' },

  msgRow: { display: 'flex', width: '100%' },
  msg: {
    maxWidth: '78%',
    background: '#fff',
    border: `1px solid ${BORDER}`,
    padding: '8px 12px',
    borderRadius: 12,
    borderTopLeftRadius: 4
  },
  msgMine: {
    background: BURG, color: '#fff', border: `1px solid ${BURG}`,
    borderTopLeftRadius: 12, borderTopRightRadius: 4
  },
  msgFrom: { fontSize: 10, color: BURG, fontWeight: 600, marginBottom: 2, letterSpacing: '0.04em' },
  msgText: { fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  msgTime: { fontSize: 10, color: '#9A9A9A', marginTop: 4, letterSpacing: '0.02em' },
  msgTimeMine: { color: 'rgba(255,255,255,0.7)' },

  inputRow: {
    borderTop: `1px solid ${BORDER}`,
    padding: 10, background: '#fff',
    display: 'flex', gap: 8, alignItems: 'flex-end'
  },
  input: {
    flex: 1, border: `1px solid ${BORDER}`, borderRadius: 10,
    padding: '10px 12px', fontSize: 13.5, fontFamily: "'DM Sans', sans-serif",
    resize: 'none', outline: 'none', background: '#fff', color: '#1C1C1C',
    minHeight: 40, maxHeight: 120
  },
  sendBtn: {
    background: BURG, color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif"
  }
};
